import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatPercentage } from "src/util/gFormatting";
import { tConnectionReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const getConnectionsReport = (start: Date, end: Date) =>
  api.get<tConnectionReport[]>("reports/connections", getParams(start, end)).then((res) => res.data);

const columns: tColumn<tConnectionReport>[] = [
  {
    id: "startDateInSeconds",
    header: "Connections Request Date",
    accessor: (originalRow) => formatDate(originalRow.startDateInSeconds),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "status",
    header: "Connection Status",
    accessor: (originalRow) => originalRow.status,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "retailerName",
    header: "Bussiness Name",
    accessor: (originalRow) => originalRow.retailerName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 220 },
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 220 },
  },
  {
    id: "salesRepName",
    header: "Rep Assigned",
    accessor: (originalRow) => originalRow.salesRepName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "salesRepCommissionFee",
    header: "Rep Commission",
    accessor: (originalRow) =>
      originalRow.salesRepCommissionFee ? formatPercentage(originalRow.salesRepCommissionFee) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "shippingFee",
    header: "Shipping",
    accessor: (originalRow) => (originalRow.shippingFee ? formatPercentage(originalRow.shippingFee) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "isTradeshowCustomer",
    header: "Tradeshow",
    accessor: (originalRow) => (originalRow.isTradeshowCustomer ? "Yes" : "No"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
];

const ConnectionsTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.connectionRequestsExcelFormat);
  const { isLoading, data } = useFocusableQuery<tConnectionReport[]>(
    useCallback(() => getConnectionsReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => item.id}
      sortedBy="startDateInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="connectionRequestsExcelFormat"
    />
  );
};

export default ConnectionsTable;
